import { Component, Inject, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { User } from '../../../core/models/user.model';
import { UserService, UserGroup, UserGroupsResponse } from '../../../core/services/user.service';

@Component({
  selector: 'app-user-groups-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatDividerModule,
    MatTooltipModule,
    MatSnackBarModule,
    ReactiveFormsModule
],
  templateUrl: './user-groups-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./user-groups-dialog.component.scss']
})
export class UserGroupsDialogComponent implements OnInit, OnDestroy {
  user: User;
  
  // Données des groupes
  userGroups: UserGroup[] = []; // Groupes de l'utilisateur choisi
  adminGroups: string[] = [];   // Groupes de l'admin connecté (chemins)
  managedGroups: string[] = []; // Groupes gérés par l'admin
  
  // Groupes filtrés pour l'affichage
  filteredAssignedGroups: any[] = []; // Groupes assignés (intersection)
  filteredAvailableGroups: any[] = []; // Groupes disponibles (différence)
  
  // Contrôles
  searchControl = new FormControl('');
  loading = false;
  processingGroups = new Set<string>();
  hasModifications = false;
  errorMessage: string = '';
  refreshing = false;
  
  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<UserGroupsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { user: User },
    private userService: UserService,
    private snackBar: MatSnackBar
  ) {
    this.user = data.user;
    // console.log('📋 UserGroupsDialog initialisé pour:', this.user);
  }

  ngOnInit(): void {
    this.loadData();
    this.setupSearch();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(searchTerm => {
        this.filterGroups(searchTerm || '');
      });
  }

  /**
   * Méthode principale pour charger les données
   * Peut être appelée depuis le template via (click)="loadData()"
   */
  async loadData(showLoading: boolean = true): Promise<void> {
    if (showLoading) {
      this.loading = true;
    }
    this.refreshing = true;
    this.errorMessage = '';
    // console.log('🔄 Chargement des données...');

    try {
      // Réinitialiser les données
      this.userGroups = [];
      this.adminGroups = [];
      this.managedGroups = [];
      this.filteredAssignedGroups = [];
      this.filteredAvailableGroups = [];

      // Charger en parallèle les groupes de l'utilisateur et de l'admin
      await Promise.all([
        this.loadUserGroups(),
        this.loadAdminGroups()
      ]);
      
      // Calculer les intersections et différences
      this.calculateGroups();
      
      // Appliquer le filtre initial
      this.filterGroups('');
    } catch (error: any) {
      console.error('❌ Erreur chargement:', error);
      this.errorMessage = error.message || 'Erreur lors du chargement des groupes';
      this.showError(this.errorMessage);
    } finally {
      if (showLoading) {
        this.loading = false;
      }
      this.refreshing = false;
    }
  }

  private async loadUserGroups(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.userService.getUserGroups(this.user.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            // Traiter les deux formats de réponse possibles
            let groupsArray: any[] = [];
            
            if (response && response.groups && Array.isArray(response.groups)) {
              // Format 1: Réponse avec objet contenant une propriété "groups"
              groupsArray = response.groups;
            } else if (response && Array.isArray(response)) {
              // Format 2: Réponse directe avec tableau de groupes
              groupsArray = response;
            } else {
              console.warn('⚠️ Format de réponse inattendu pour getUserGroups:', response);
            }
            
            // Conserver le NOM ORIGINAL des groupes (ne pas modifier la casse)
            this.userGroups = groupsArray.map((group: any) => ({
              id: group.id,
              name: group.name, // Conserver le nom original
              originalName: group.name, // Garder une copie du nom original
              path: group.path || group.name,
              originalPath: group.path || group.name // Garder une copie du chemin original
            }));
            
            resolve();
          },
          error: (error) => {
            console.error('❌ Erreur groupes utilisateur:', error);
            
            let errorMsg = 'Impossible de charger les groupes de l\'utilisateur';
            if (error.error) {
              if (typeof error.error === 'string') {
                errorMsg = error.error;
              } else if (error.error.message) {
                errorMsg = error.error.message;
              } else if (error.error.error) {
                errorMsg = error.error.error;
              }
            } else if (error.message) {
              errorMsg = error.message;
            }
            
            reject(new Error(errorMsg));
          }
        });
    });
  }

  private async loadAdminGroups(): Promise<void> {
    return new Promise((resolve, reject) => {
      // console.log('👨‍💼 Chargement groupes de l\'admin...');
      
      this.userService.getMyGroups()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: UserGroupsResponse) => {
            if (response) {
              // Conserver les CHEMINS ORIGINAUX (ne pas modifier la casse)
              this.adminGroups = response.managedGroups && response.managedGroups.length > 0 
                ? response.managedGroups 
                : response.groups || [];
              
              this.managedGroups = response.managedGroups || [];
              
              // Si adminGroups est vide, utiliser les groups normaux
              if (this.adminGroups.length === 0 && response.groups && response.groups.length > 0) {
                this.adminGroups = response.groups;
              }
            } else {
              console.warn('⚠️ Réponse getMyGroups vide');
              this.adminGroups = [];
              this.managedGroups = [];
            }
            
            resolve();
          },
          error: (error) => {
            console.error('❌ Erreur groupes admin:', error);
            
            let errorMsg = 'Impossible de charger les groupes de l\'admin';
            if (error.error) {
              if (typeof error.error === 'string') {
                errorMsg = error.error;
              } else if (error.error.message) {
                errorMsg = error.error.message;
              } else if (error.error.error) {
                errorMsg = error.error.error;
              }
            } else if (error.message) {
              errorMsg = error.message;
            }
            
            reject(new Error(errorMsg));
          }
        });
    });
  }

  private calculateGroups(): void {
    // console.log(' Calcul des intersections et différences (avec noms originaux)...');
    
    // 1. Normaliser les chemins des groupes SANS CHANGER LA CASSE
    const normalizePath = (path: string): string => {
      if (!path || path.trim() === '') return '';
      // Retirer les slashes en début et fin, mais CONSERVER LA CASSE ORIGINALE
      return path.replace(/^\/+|\/+$/g, '').trim();
    };
    
    // 2. Créer des maps pour les comparaisons (avec path normalisé comme clé)
    const userGroupMap = new Map();
    this.userGroups.forEach(group => {
      const path = normalizePath(group.path);
      const key = path || normalizePath(group.name);
      if (key) {
        userGroupMap.set(key.toLowerCase(), group); // Utiliser lowercase seulement pour la clé de comparaison
      }
    });
    
    const adminGroupSet = new Set(
      this.adminGroups.map(path => normalizePath(path)).filter(p => p)
    );
    
    const managedGroupSet = new Set(
      this.managedGroups.map(path => normalizePath(path)).filter(p => p)
    );
    
    // 3. Groupes assignés: intersection entre userGroups et adminGroups
    // Filtrer seulement les groupes qui appartiennent aux groupes gérés par l'admin
    this.filteredAssignedGroups = Array.from(userGroupMap.values())
      .filter(userGroup => {
        const userGroupPath = normalizePath(userGroup.path || userGroup.name);
        if (!userGroupPath) return false;
        
        // Vérifier si le groupe est dans les groupes admin (soit directement, soit comme sous-groupe)
        let isInAdmin = false;
        for (const adminPath of adminGroupSet) {
          if (adminPath && 
              (userGroupPath.toLowerCase() === adminPath.toLowerCase() || 
               userGroupPath.toLowerCase().startsWith(adminPath.toLowerCase() + '/'))) {
            isInAdmin = true;
            break;
          }
        }
        
        // Si pas de managedGroups, on prend tout
        if (managedGroupSet.size === 0) {
          return isInAdmin;
        }
        
        // Vérifier si le groupe est dans les groupes gérés
        let isInManaged = false;
        for (const managedPath of managedGroupSet) {
          if (managedPath && 
              (userGroupPath.toLowerCase() === managedPath.toLowerCase() || 
               userGroupPath.toLowerCase().startsWith(managedPath.toLowerCase() + '/'))) {
            isInManaged = true;
            break;
          }
        }
        
        return isInAdmin && isInManaged;
      })
      .map(group => ({
        ...group,
        displayPath: this.getGroupDisplayPath(group),
        // S'assurer que le nom est le nom ORIGINAL
        name: group.originalName || group.name
      }));
    
    // 4. Groupes disponibles: adminGroups qui ne sont PAS dans userGroups
    this.filteredAvailableGroups = Array.from(adminGroupSet)
      .filter(adminPath => {
        if (!adminPath) return false;
        
        // Vérifier que ce groupe n'est pas déjà assigné à l'utilisateur
        let isAlreadyAssigned = false;
        for (const userGroupKey of userGroupMap.keys()) {
          if (userGroupKey && 
              (userGroupKey === adminPath.toLowerCase() || 
               userGroupKey.startsWith(adminPath.toLowerCase() + '/'))) {
            isAlreadyAssigned = true;
            break;
          }
        }
        
        // Pour les managedGroups, vérifier l'autorisation
        if (managedGroupSet.size > 0) {
          let isAuthorized = false;
          for (const managedPath of managedGroupSet) {
            if (managedPath && 
                (adminPath.toLowerCase() === managedPath.toLowerCase() || 
                 adminPath.toLowerCase().startsWith(managedPath.toLowerCase() + '/'))) {
              isAuthorized = true;
              break;
            }
          }
          return !isAlreadyAssigned && isAuthorized;
        }
        
        // Pas de restriction de managedGroups
        return !isAlreadyAssigned;
      })
      .map(path => {
        // Extraire le nom ORIGINAL du groupe depuis le chemin
        const name = this.extractGroupNameFromPath(path, true); // true = conserver la casse
        return {
          name: name,
          originalName: name, // Conserver le nom original
          path: path,
          originalPath: path, // Conserver le chemin original
          displayPath: this.getGroupDisplayPath({ path: path, name: name })
        };
      });
    
    // Debug: afficher quelques noms pour vérifier
    if (this.filteredAvailableGroups.length > 0) {
      // console.log('🔍 Exemples de noms disponibles:', 
      //   this.filteredAvailableGroups.slice(0, 3).map(g => g.name)
      // );
    }
  }

  private extractGroupNameFromPath(path: string, preserveCase: boolean = true): string {
    if (!path) return '';
    // Extraire le dernier segment du chemin
    const segments = path.split('/').filter(segment => segment.trim() !== '');
    const name = segments.length > 0 ? segments[segments.length - 1] : path;
    
    // Retourner le nom tel quel (avec sa casse d'origine)
    return preserveCase ? name : name.toLowerCase();
  }

  private filterGroups(searchTerm: string): void {
    const lowerSearch = searchTerm.toLowerCase().trim();
    
    if (!lowerSearch) {
      return; // Pas besoin de filtrer, on garde les groupes calculés
    }
    
    // Filtrer les groupes assignés (comparaison insensible à la casse)
    const filteredAssigned = this.filteredAssignedGroups.filter(group => {
      const name = (group.name || '').toLowerCase();
      const path = (group.path || '').toLowerCase();
      const displayPath = (group.displayPath || '').toLowerCase();
      
      return name.includes(lowerSearch) || 
             path.includes(lowerSearch) || 
             displayPath.includes(lowerSearch);
    });
    
    // Filtrer les groupes disponibles (comparaison insensible à la casse)
    const filteredAvailable = this.filteredAvailableGroups.filter(group => {
      const name = (group.name || '').toLowerCase();
      const path = (group.path || '').toLowerCase();
      const displayPath = (group.displayPath || '').toLowerCase();
      
      return name.includes(lowerSearch) || 
             path.includes(lowerSearch) || 
             displayPath.includes(lowerSearch);
    });
    
    // Mettre à jour les tableaux filtrés
    this.filteredAssignedGroups = filteredAssigned;
    this.filteredAvailableGroups = filteredAvailable;
  }

  isProcessing(groupName: string): boolean {
    return this.processingGroups.has(groupName);
  }

  async addToGroup(group: any): Promise<void> {
    if (this.isProcessing(group.name)) {
      return;
    }
    this.processingGroups.add(group.name);

    // Utiliser le NOM ORIGINAL du groupe (avec sa casse d'origine)
    const groupName = group.originalName || group.name;
    
    this.userService.addUserToGroup(this.user.id, groupName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.showSuccess(`Utilisateur ajouté au groupe "${group.name}"`);
          this.hasModifications = true;
          
          // Recharger les données sans afficher le loader global
          this.loadData(false).then(() => {
            this.processingGroups.delete(group.name);
          });
        },
        error: (error) => {
          console.error('❌ Erreur ajout:', error);
          console.error('Détails de l\'erreur:', {
            userId: this.user.id,
            groupName: groupName,
            originalGroup: group,
            error: error
          });
          this.handleGroupError(error, group.name, 'ajout');
          this.processingGroups.delete(group.name);
        }
      });
  }

  confirmRemoveFromGroup(group: any): void {
    const confirmed = confirm(
      `⚠️ Confirmation requise\n\nÊtes-vous sûr de vouloir retirer ${this.user.firstName} ${this.user.lastName} du groupe "${group.name}" ?\n\nCette action est réversible.`
    );

    if (confirmed) {
      this.removeFromGroup(group);
    }
  }

  private async removeFromGroup(group: any): Promise<void> {
    if (this.isProcessing(group.name)) {
      // console.log('⏳ Traitement déjà en cours pour:', group.name);
      return;
    }

    this.processingGroups.add(group.name);

    // Utiliser le NOM ORIGINAL du groupe (avec sa casse d'origine)
    const groupName = group.originalName || group.name;
    
    this.userService.removeUserFromGroup(this.user.id, groupName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.showSuccess(`Utilisateur retiré du groupe "${group.name}"`);
          this.hasModifications = true;
          
          // Recharger les données sans afficher le loader global
          this.loadData(false).then(() => {
            this.processingGroups.delete(group.name);
          });
        },
        error: (error) => {
          console.error('❌ Erreur retrait:', error);
          console.error('Détails de l\'erreur:', {
            userId: this.user.id,
            groupName: groupName,
            originalGroup: group,
            error: error
          });
          this.handleGroupError(error, group.name, 'retrait');
          this.processingGroups.delete(group.name);
        }
      });
  }

  private handleGroupError(error: any, groupName: string, action: string): void {
    let message = `Erreur lors du ${action} du groupe "${groupName}"`;
    
    if (error.status === 403) {
      message = 'Vous n\'avez pas l\'autorisation de gérer ce groupe';
    } else if (error.status === 409) {
      message = action === 'ajout' 
        ? 'L\'utilisateur est déjà membre de ce groupe'
        : 'L\'utilisateur n\'est pas membre de ce groupe';
    } else if (error.status === 404) {
      message = 'Groupe ou utilisateur non trouvé';
    } else if (error.status === 400) {
      const errorDetail = error.error?.message || error.error?.error || error.message;
      message = `Requête invalide: ${errorDetail}`;
    } else if (error.status === 500) {
      message = 'Erreur serveur interne';
    } else if (error.error && typeof error.error === 'string') {
      message = error.error;
    } else if (error.error && error.error.error) {
      message = error.error.error;
    } else if (error.message) {
      message = error.message;
    }
    
    this.showError(message);
  }

  getGroupDisplayPath(group: any): string {
    if (!group) return '';
    
    // Utiliser le chemin ORIGINAL (avec sa casse d'origine)
    const path = group.originalPath || group.path || group.name || '';
    if (!path) return '';
    
    // Afficher un chemin lisible sans le slash initial
    return path.startsWith('/') ? path.substring(1) : path;
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.filterGroups('');
  }

  close(): void {
    this.dialogRef.close({ 
      modified: this.hasModifications,
      user: this.user
    });
  }

  getInitials(): string {
    if (!this.user) return '';
    const first = this.user.firstName?.charAt(0) || '';
    const last = this.user.lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || '?';
  }

  refreshData(): void {
    this.loadData();
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 3000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 5000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }
}